import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WorkflowNode } from './WorkflowNode';
import { CanvasToolbar } from './CanvasToolbar';
import { BlockPaletteMenu, defaultStartTemplate } from '@/components/BlockPaletteMenu';
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from '@/components/ui/context-menu';
import { Button } from '@/components/ui/button';
import { SWISS_ARMY_BLOCK_HELP } from '@/lib/blockContext';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { buildNodeFromTemplate, canPlaceStartBlock, type BlockTemplate } from '@/lib/blockPalette';
import {
  CanvasViewportContext,
  CANVAS_WORLD_HEIGHT,
  CANVAS_WORLD_WIDTH,
  screenToWorld,
  viewportScale,
} from '@/lib/canvasViewport';
import {
  buildMapEdgePath,
  buildMapJourneyTubePath,
  getMapNodeDimensions,
} from '@/lib/mapVisuals';
import {
  resolveMapFlowOrientation,
  type ResolvedMapFlowOrientation,
} from '@/lib/mapFlowOrientation';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useMissionWorkspaceOptional } from '@/contexts/MissionWorkspaceContext';
import { analyzeInlineValidation } from '@/services/inlineValidation';
import { useExecutionTraceStore } from '@/stores/executionTraceStore';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';

type PanSession = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startPanX: number;
  startPanY: number;
  moved: boolean;
};

const PAN_CLICK_THRESHOLD = 4;

export function Canvas() {
  const viewportRef = useRef<HTMLDivElement>(null);
  const {
    currentWorkflow,
    addNode,
    selectNode,
    selectedNode,
    deleteNode,
    cancelConnect,
    connectFromNodeId,
    applyFlowLayout,
    undoMap,
    redoMap,
  } = useWorkflowStore();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [isPanning, setIsPanning] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [viewportSize, setViewportSize] = useState({ width: 1024, height: 768 });
  const panSessionRef = useRef<PanSession | null>(null);
  const lastAutoLayoutOrientation = useRef<ResolvedMapFlowOrientation | null>(null);
  const lastAutoLayoutWorkflowId = useRef<string | null>(null);
  const trace = useExecutionTraceStore((s) =>
    currentWorkflow?.id ? s.traces[currentWorkflow.id] : undefined
  );

  const flowOrientationPref = currentWorkflow?.missionMeta?.flowOrientation ?? 'auto';
  const flowTracingMode = currentWorkflow?.missionMeta?.flowTracingMode === true;
  const effectiveFlowOrientation = useMemo(
    () => resolveMapFlowOrientation(flowOrientationPref, viewportSize.width, viewportSize.height),
    [flowOrientationPref, viewportSize.width, viewportSize.height]
  );

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const updateSize = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setViewportSize((prev) => {
        if (prev.width === w && prev.height === h) return prev;
        return { width: w, height: h };
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentWorkflow?.id]);

  useEffect(() => {
    if (!currentWorkflow || currentWorkflow.nodes.length === 0) return;
    if (flowOrientationPref !== 'auto') return;

    const workflowChanged = lastAutoLayoutWorkflowId.current !== currentWorkflow.id;
    const orientationChanged = lastAutoLayoutOrientation.current !== effectiveFlowOrientation;

    if (!workflowChanged && !orientationChanged) return;

    if (workflowChanged) {
      applyFlowLayout(effectiveFlowOrientation, { recordUndo: false, mode: 'initial' });
    } else if (orientationChanged && lastAutoLayoutOrientation.current) {
      applyFlowLayout(effectiveFlowOrientation, {
        recordUndo: false,
        mode: 'reorient',
        fromOrientation: lastAutoLayoutOrientation.current,
      });
    }

    lastAutoLayoutOrientation.current = effectiveFlowOrientation;
    lastAutoLayoutWorkflowId.current = currentWorkflow.id;
  }, [
    currentWorkflow?.id,
    currentWorkflow?.nodes.length,
    flowOrientationPref,
    effectiveFlowOrientation,
    applyFlowLayout,
  ]);

  const viewport = useMemo(
    () => ({ panX: pan.x, panY: pan.y, zoom }),
    [pan.x, pan.y, zoom]
  );

  const viewportContextValue = useMemo(
    () => ({ viewport, containerRef: viewportRef }),
    [viewport]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        setSpaceHeld(true);
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNode && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
          e.preventDefault();
          deleteNode(selectedNode);
        }
      }
      if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        const mod = e.metaKey || e.ctrlKey;
        if (mod && e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) {
            if (redoMap()) toast.message('Redid map change');
          } else if (undoMap()) {
            toast.message('Undid last map change');
          }
          return;
        }
        if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          setZoom((z) => Math.max(z - 10, 50));
        } else if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          setZoom((z) => Math.min(z + 10, 200));
        }
      }
      if (e.key === 'Escape') {
        if (connectFromNodeId) {
          cancelConnect();
          toast.message('Link cancelled');
        }
        selectNode(null);
        panSessionRef.current = null;
        setIsPanning(false);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [selectedNode, deleteNode, selectNode, cancelConnect, connectFromNodeId, undoMap, redoMap]);

  const resolvePlacement = useCallback(
    (clientX?: number, clientY?: number) => {
      const rect = viewportRef.current?.getBoundingClientRect();
      if (!rect) return { x: 80, y: 80 };

      if (clientX !== undefined && clientY !== undefined) {
        const world = screenToWorld(clientX, clientY, rect, viewport);
        return { x: Math.max(20, world.x), y: Math.max(20, world.y) };
      }

      const world = screenToWorld(rect.left + rect.width / 2, rect.top + rect.height / 2, rect, viewport);
      return { x: Math.max(20, world.x), y: Math.max(20, world.y) };
    },
    [viewport]
  );

  const handleAddNode = useCallback(
    (template: BlockTemplate, clientX?: number, clientY?: number) => {
      if (!currentWorkflow) return;

      if (template.type === 'start' && !canPlaceStartBlock(currentWorkflow)) {
        toast.message('This Mission already has a START block');
        setContextMenu(null);
        return;
      }

      const { x, y } = resolvePlacement(
        clientX ?? contextMenu?.x,
        clientY ?? contextMenu?.y
      );

      addNode(buildNodeFromTemplate(template, currentWorkflow, x, y));
      setContextMenu(null);
      toast.success(`${template.label} added`);
    },
    [addNode, contextMenu, currentWorkflow, resolvePlacement]
  );

  const finishPanClick = useCallback(() => {
    if (connectFromNodeId) {
      cancelConnect();
      toast.message('Link cancelled');
    }
    selectNode(null);
  }, [cancelConnect, connectFromNodeId, selectNode]);

  const handlePanPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 && e.button !== 1) return;
      if ((e.target as HTMLElement).closest('[data-workflow-node]')) return;
      if ((e.target as HTMLElement).closest('[data-canvas-ui]')) return;

      const useLeftPan = e.button === 0 && !spaceHeld;
      const useSpacePan = e.button === 0 && spaceHeld;
      const useMiddlePan = e.button === 1;
      if (!useLeftPan && !useSpacePan && !useMiddlePan) return;

      if (useMiddlePan) e.preventDefault();

      panSessionRef.current = {
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startPanX: pan.x,
        startPanY: pan.y,
        moved: false,
      };
      setIsPanning(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pan.x, pan.y, spaceHeld]
  );

  const handlePanPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const session = panSessionRef.current;
    if (!session || session.pointerId !== e.pointerId) return;

    const dx = e.clientX - session.startClientX;
    const dy = e.clientY - session.startClientY;

    if (!session.moved && Math.hypot(dx, dy) >= PAN_CLICK_THRESHOLD) {
      session.moved = true;
    }

    if (session.moved) {
      setPan({ x: session.startPanX + dx, y: session.startPanY + dy });
    }
  }, []);

  const handlePanPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const session = panSessionRef.current;
      if (!session || session.pointerId !== e.pointerId) return;

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }

      if (!session.moved && e.button === 0 && !spaceHeld) {
        finishPanClick();
      }

      panSessionRef.current = null;
      setIsPanning(false);
    },
    [finishPanClick, spaceHeld]
  );

  const handleZoomIn = useCallback(() => setZoom((z) => Math.min(z + 10, 200)), []);
  const handleZoomOut = useCallback(() => setZoom((z) => Math.max(z - 10, 50)), []);

  const missionWorkspace = useMissionWorkspaceOptional();
  const mapToolsInNav = Boolean(missionWorkspace);

  useEffect(() => {
    if (!missionWorkspace) return;
    missionWorkspace.setMapTools({
      zoom,
      onZoomIn: handleZoomIn,
      onZoomOut: handleZoomOut,
      onAddBlock: handleAddNode,
      flowOrientation: flowOrientationPref,
      effectiveFlowOrientation,
    });
    return () => missionWorkspace.setMapTools(null);
  }, [
    missionWorkspace,
    zoom,
    handleZoomIn,
    handleZoomOut,
    handleAddNode,
    flowOrientationPref,
    effectiveFlowOrientation,
  ]);

  const scale = viewportScale(viewport);
  const worldTransform = `translate(${pan.x}px, ${pan.y}px) scale(${scale})`;
  const inlineValidation = useMemo(
    () => (currentWorkflow ? analyzeInlineValidation(currentWorkflow) : null),
    [currentWorkflow]
  );
  const failpointSet = useMemo(
    () => new Set(flowTracingMode ? (inlineValidation?.failpointNodeIds ?? []) : []),
    [inlineValidation, flowTracingMode]
  );
  const liveFailpointSet = useMemo(() => {
    const ids = new Set(failpointSet);
    if (trace?.failedNodeId) ids.add(trace.failedNodeId);
    return ids;
  }, [failpointSet, trace?.failedNodeId]);
  const disconnectedSet = useMemo(
    () => new Set(flowTracingMode ? (inlineValidation?.disconnectedNodeIds ?? []) : []),
    [inlineValidation, flowTracingMode]
  );
  const smartInputSet = useMemo(() => {
    const ids = new Set<string>();
    if (!currentWorkflow) return ids;
    currentWorkflow.nodes.forEach((node) => {
      if (getSmartBlockInputRequirements(node).length > 0) ids.add(node.id);
    });
    return ids;
  }, [currentWorkflow]);

  if (!currentWorkflow) {
    return (
      <div className="ab-map-viewport h-full min-h-0 flex items-center justify-center">
        <div className="relative z-[1] text-center space-y-3 px-6">
          <p className="text-muted-foreground text-lg font-medium">
            No {PRODUCT_TERMS.autoBlockFlow.toLowerCase()} selected
          </p>
          <p className="text-muted-foreground text-sm">
            Use <strong>New flow</strong> in the header, pick a flow in the run bar, or plan one in{' '}
            {PRODUCT_TERMS.autoBlockFlowComposer}.
          </p>
        </div>
      </div>
    );
  }

  const emptyCanvas = currentWorkflow.nodes.length === 0;
  const startTemplate = defaultStartTemplate();

  return (
    <CanvasViewportContext.Provider value={viewportContextValue}>
      <div className="h-full min-h-0 w-full">
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div
            ref={viewportRef}
            role="application"
            aria-label={PRODUCT_TERMS.mission}
            className={cn(
              'ab-map-viewport h-full min-h-0 relative overflow-hidden touch-none',
              isPanning || spaceHeld ? 'cursor-grabbing' : 'cursor-grab'
            )}
            onPointerDown={handlePanPointerDown}
            onPointerMove={handlePanPointerMove}
            onPointerUp={handlePanPointerUp}
            onPointerCancel={handlePanPointerUp}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu({ x: e.clientX, y: e.clientY });
            }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 'var(--map-z-ui)' }} data-canvas-ui>
              {!mapToolsInNav && (
                <CanvasToolbar
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  zoom={zoom}
                  workflow={currentWorkflow}
                  onAddBlock={(template) => handleAddNode(template)}
                  flowOrientation={flowOrientationPref}
                  effectiveFlowOrientation={effectiveFlowOrientation}
                />
              )}

              {connectFromNodeId && (
                <div className="absolute top-16 left-4 max-w-xs rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-foreground shadow-sm pointer-events-none">
                  <strong>Link mode:</strong> click another block to connect or remove an existing link. Esc cancels.
                </div>
              )}

              {spaceHeld && (
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-border bg-card/90 px-3 py-1 text-[11px] text-muted-foreground shadow-sm">
                  Space + drag to pan
                </div>
              )}
            </div>

            <div
              className="ab-map-world"
              style={{
                width: CANVAS_WORLD_WIDTH,
                height: CANVAS_WORLD_HEIGHT,
                transform: worldTransform,
              }}
            >
              <div className="ab-map-world-texture" aria-hidden />

              <svg
                className="ab-map-world-layer ab-map-grid top-0 left-0"
                width={CANVAS_WORLD_WIDTH}
                height={CANVAS_WORLD_HEIGHT}
              >
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>

              <svg
                className="ab-map-world-edges ab-map-edges-svg"
                width={CANVAS_WORLD_WIDTH}
                height={CANVAS_WORLD_HEIGHT}
              >
                {currentWorkflow.edges.map((edge) => {
                  const fromNode = currentWorkflow.nodes.find((n) => n.id === edge.from);
                  const toNode = currentWorkflow.nodes.find((n) => n.id === edge.to);
                  if (!fromNode || !toNode) return null;

                  const fromDim = getMapNodeDimensions(fromNode);
                  const toDim = getMapNodeDimensions(toNode);
                  const isJourneyTube = edge.kind === 'journey_tube';

                  if (isJourneyTube) {
                    const { path, labelX, labelY } = buildMapJourneyTubePath(
                      fromNode,
                      toNode,
                      fromDim.width,
                      fromDim.height,
                      {
                        toWidth: toDim.width,
                        toHeight: toDim.height,
                        orientation: effectiveFlowOrientation,
                      }
                    );
                    return (
                      <g key={edge.id}>
                        <path
                          d={path}
                          fill="none"
                          className="ab-map-edge-path ab-map-edge-path--tube"
                          strokeWidth="2"
                        />
                        <text x={labelX} y={labelY} textAnchor="middle" className="ab-map-tube-label">
                          Journey Tube
                        </text>
                      </g>
                    );
                  }

                  return (
                    <path
                      key={edge.id}
                      d={buildMapEdgePath(
                        fromNode,
                        toNode,
                        fromDim.width,
                        fromDim.height,
                        {
                          toWidth: toDim.width,
                          toHeight: toDim.height,
                          orientation: effectiveFlowOrientation,
                        }
                      )}
                      className="ab-map-edge-path"
                    />
                  );
                })}
              </svg>

              <div className="ab-map-world-nodes">
                {currentWorkflow.nodes.map((node) => (
                <WorkflowNode
                  key={node.id}
                  node={node}
                  inlineFailpoint={liveFailpointSet.has(node.id)}
                  inlineDisconnected={disconnectedSet.has(node.id)}
                  smartNeedsInput={smartInputSet.has(node.id) && !liveFailpointSet.has(node.id)}
                />
                ))}

                {emptyCanvas && (
                  <div className="absolute inset-0 flex items-center justify-center px-6 pointer-events-none">
                    <div className="text-center space-y-4 max-w-md pointer-events-auto">
                      <p className="text-muted-foreground text-sm">
                        {SWISS_ARMY_BLOCK_HELP} Add blocks manually below, or use{' '}
                        {PRODUCT_TERMS.autoBlockFlowComposer} to generate a plan.
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {startTemplate && canPlaceStartBlock(currentWorkflow) && (
                          <Button size="sm" onClick={() => handleAddNode(startTemplate)}>
                            Add START block
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAddNode({ type: 'goal', subtype: 'outcome', label: 'Goal — outcome' })}
                        >
                          Add GOAL block
                        </Button>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Drag empty map to pan · Space + drag · pinch zoom via toolbar. <strong>Add block</strong> or
                        right-click to place. <strong>+</strong> on a block to link/unlink.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56 max-h-[min(70vh,480px)] overflow-y-auto">
          <BlockPaletteMenu
            workflow={currentWorkflow}
            onPick={(template) => handleAddNode(template)}
          />
        </ContextMenuContent>
      </ContextMenu>
      </div>
    </CanvasViewportContext.Provider>
  );
}
